import { Box, Button, Icon, IconButton, Modal, Stack, Typography } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdCancel } from 'react-icons/md'
import { ColumnOptionRow, ColumnSelectOption } from './ColumnOptionRow'
import {
  ColorAndTitle,
  ColumnOption,
  ColumnSelector,
  OpenTitle,
} from './GenericTable.style'

interface IColumnOptionsProps {
  id: string
  open: boolean
  handleClose: () => void
  columns: GridColumns
  fixedColumns: IColumnForOption[]
  actionColumns: IColumnForOption[]
  columnOptions: IColumnForOption[]
  setColumnOptions: (columnOptions: IColumnForOption[]) => void
  columnArray: IColumnForOption[]
  setColumnArray: React.Dispatch<React.SetStateAction<IColumnForOption[]>>
  setColumns: (columns: GridColumns) => void
}

export const ColumnOptions: React.FC<IColumnOptionsProps> = ({
  id,
  open,
  handleClose,
  columns,
  fixedColumns,
  actionColumns,
  columnOptions,
  setColumnOptions,
  columnArray,
  setColumnArray,
  setColumns,
}) => {
  const { t } = useTranslation(['generic-table', 'common'])
  const actionsLabel = t('actions', { ns: 'common' })
  const [optionColumns, setOptionColumns] = useState<IColumnForOption[]>(() => [...columnOptions])
  const columnArrayRef = useRef(columnArray)
  columnArrayRef.current = columnArray

  useEffect(() => {
    if (open) {
      setOptionColumns([...columnOptions])
    }
  }, [open, columnOptions])

  const getColumnDisplayName = useCallback(
    (fieldId: string): string => {
      const column = columns.find(c => c.field === fieldId)
      return (column?.headerName as string) || fieldId
    },
    [columns],
  )

  const selectOptions: ColumnSelectOption[] = useMemo(
    () =>
      columnArray
        .filter(f => f.field && f.field !== 'id' && !actionColumns.some(c => c.field === f.field))
        .map(f => ({ field: f.field, label: f.headerName })),
    [columnArray, actionColumns],
  )

  const handleOptionFieldChange = useCallback((previousField: string, newField: string) => {
    const previousColumn = columns.find(c => c.field === previousField)
    setOptionColumns(prev => {
      const idx = prev.findIndex(c => c.field === previousField)
      if (idx === -1) return prev
      const next = [...prev]
      next[idx] = { field: newField, headerName: getColumnDisplayName(newField) }
      return next
    })
    setColumnArray(prev => {
      const pool = prev.filter(f => f.field !== newField)
      const canReturn =
        previousField &&
        previousField !== 'id' &&
        !fixedColumns.some(c => c.field === previousField) &&
        !actionColumns.some(c => c.field === previousField)
      if (canReturn && !pool.some(c => c.field === previousField)) {
        return [{ field: previousColumn?.field, headerName: previousColumn?.headerName }, ...pool] as IColumnForOption[]
      }
      return pool
    })
  }, [actionColumns, columns, fixedColumns])

  const handleRemoveOption = useCallback(
    (field: string) => {
      if (field === 'id' || actionColumns.some(c => c.field === field)) return
      setOptionColumns(prev => prev.filter(c => c.field !== field))
      setColumnArray(prev => (prev.some(c => c.field === field) ? prev : [...prev, { field, headerName: getColumnDisplayName(field) }]))
    },
    [actionColumns, getColumnDisplayName, setColumnArray],
  )

  const handleAddColumn = useCallback(() => {
    const prev = columnArrayRef.current
    const available = prev.filter(a => a.field !== 'id' && !actionColumns.some(c => c.field === a.field))
    if (available.length === 0) return
    const first = available[0]
    const takeIdx = prev.findIndex(p => p.field === first.field)
    const next = takeIdx === -1 ? prev : [...prev.slice(0, takeIdx), ...prev.slice(takeIdx + 1)]
    columnArrayRef.current = next
    setColumnArray(next)
    setOptionColumns(o => [...o, { field: first.field, headerName: first.headerName }])
  }, [actionColumns, setColumnArray])

  const handleSave = useCallback(() => {
    const ordered = columns.filter(f => optionColumns.some(c => c.field === f.field) && !fixedColumns.some(c => c.field === f.field) && !actionColumns.some(c => c.field === f.field)).sort((a, b) => optionColumns.findIndex(c => c.field === a.field) - optionColumns.findIndex(c => c.field === b.field))
      .sort((a, b) => optionColumns.findIndex(c => c.field === a.field) - optionColumns.findIndex(c => c.field === b.field))
    setColumns(ordered)
    setColumnOptions([...optionColumns])
    setColumnArray(prev => prev.filter(c => !optionColumns.some(o => o.field === c.field)))
    try {
      localStorage.setItem(`columnOptions-${id}`, JSON.stringify(optionColumns))
    } catch {
      /* ignore quota / private mode */
    }
    handleClose()
  }, [
    actionColumns,
    columns,
    fixedColumns,
    handleClose,
    id,
    optionColumns,
    setColumnArray,
    setColumnOptions,
    setColumns,
  ])

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="column-options-title"
      aria-describedby="column-options-description"
    >
      <ColumnSelector>
        <Stack direction="column" justifyContent="space-between" spacing={2} height="100%">
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} width="100%">
              <Typography id="column-options-title" variant="h6">
                {t('column-options')}
              </Typography>
              <ColorAndTitle
                component={IconButton}
                onClick={handleClose}
                aria-label={t('cancel', { ns: 'common' })}
              >
                <Icon>
                  <MdCancel />
                </Icon>
              </ColorAndTitle>
            </Stack>
            <ColumnOption direction="column" justifyContent="center" alignItems="flex-start" spacing={2}>
              <OpenTitle id="column-options-description" variant="body1">
                {t('add-or-remove-columns')}
              </OpenTitle>
              {fixedColumns.map((field, index) => (
                <ColumnOptionRow
                  key={`fixed-${field.field}-${index}`}
                  rowId={`fixed-${index}-${field.field}`}
                  field={field.field}
                  headerName={field.headerName}
                  selectOptions={selectOptions}
                  actionsLabel={actionsLabel}
                  isColumnOption={false}
                  onFieldChange={handleOptionFieldChange}
                  onRemove={handleRemoveOption}
                />
              ))}
              {optionColumns
                .filter(field => field.field !== 'id')
                .map(field => (
                  <ColumnOptionRow
                    key={`opt-${field.field}`}
                    rowId={`opt-${field.field}`}
                    field={field.field}
                    headerName={field.headerName}
                    isColumnOption
                    selectOptions={selectOptions}
                    actionsLabel={actionsLabel}
                    onFieldChange={handleOptionFieldChange}
                    onRemove={handleRemoveOption}
                  />
                ))}
              {actionColumns.map((field, index) => (
                <ColumnOptionRow
                  key={`action-${field.field}-${index}`}
                  rowId={`action-${index}-${field.field}`}
                  field={field.field}
                  headerName={field.headerName}
                  selectOptions={selectOptions}
                  actionsLabel={actionsLabel}
                  isColumnOption={false}
                  onFieldChange={handleOptionFieldChange}
                  onRemove={handleRemoveOption}
                />
              ))}
              {columnArray.some(f => f.field !== 'id' && !actionColumns.some(c => c.field === f.field)) && (
                <Box
                  component="button"
                  type="button"
                  onClick={handleAddColumn}
                  sx={{
                    cursor: 'pointer',
                    border: 'none',
                    background: 'none',
                    padding: 0,
                    textAlign: 'left',
                  }}
                >
                  <OpenTitle variant="body1">{`+ ${t('add-column')}`}</OpenTitle>
                </Box>
              )}
            </ColumnOption>
          </Box>
          <Stack direction="row" justifyContent="flex-end" alignItems="flex-end" spacing={2} width="100%">
            <Button variant="contained" onClick={handleSave}>
              {t('save', { ns: 'common' })}
            </Button>
            <Button onClick={handleClose}>{t('cancel', { ns: 'common' })}</Button>
          </Stack>
        </Stack>
      </ColumnSelector>
    </Modal>
  )
}


export interface IColumnForOption {
  field: string
  headerName: string
}